import type { CSSProperties } from "react";
import { Link } from "react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Search, X } from "lucide-react";
import { JOIN_URL } from "@/lib/club";
import { useMenu } from "../ConceptBits";
import { CLUB, Draw, EASE, HOME, Heart, Note, Torn, useArchMode } from "./bits";
import "./Hero.css";

/* Hero, nav, and phone menu. Scene positions are mockup pixels (1672 × 941);
   the right-hand scene is its own frame (mockup x 600–1672). */

const S = { x: 600, w: 1072, h: 941 };
const sbox = (x: number, y: number, w?: number, h?: number): CSSProperties => ({
  left: `${((x - S.x) / S.w) * 100}%`, top: `${(y / S.h) * 100}%`,
  ...(w !== undefined && { width: `${(w / S.w) * 100}%` }),
  ...(h !== undefined && { height: `${(h / S.h) * 100}%` }),
});

/* ─── Hero ─── */

const MENU = [
  { to: HOME, label: "Home", tone: "blue" },
  { to: "/about", label: "About", tone: "pink" },
  { to: "/videos", label: "Projects", tone: "ink" },
  { to: CLUB, label: "The club", tone: "blue" },
  { to: "/guides", label: "Journal", tone: "pink" },
  { to: "/shop", label: "Shop", tone: "ink" },
] as const;

/** Phone menu: a paper sheet that drops down, with the hero's torn strong paper at its foot. */
function Menu({ menu }: { menu: ReturnType<typeof useMenu> }) {
  return <AnimatePresence>
    {menu.open && <motion.div id="p2-menu" ref={menu.panel} className="p2-menu" role="dialog" aria-modal="true" aria-label="Menu"
      initial={{ y: "-100%" }} animate={{ y: 0 }} exit={{ y: "-100%" }} transition={{ duration: 0.5, ease: EASE }}>
      <svg className="p2-menu-paper" viewBox="0 0 400 220" preserveAspectRatio="none" aria-hidden="true">
        <Torn seed={5} fill="var(--k-strong)" fiber={5} edge={[[400, 70], [300, 92], [190, 118], [90, 150], [0, 176]]} close={[[0, 220], [400, 220]]} />
      </svg>
      <div className="p2-menu-top">
        <Link to={HOME} className="p2-logo">A Few<br />Loose Threads</Link>
        <button type="button" className="p2-burger" onClick={() => menu.setOpen(false)} aria-label="Close menu"><X size={22} aria-hidden="true" /></button>
      </div>
      <nav aria-label="Main">
        <ol>{MENU.map((m, i) => <motion.li key={m.label} initial={{ opacity: 0, x: -24 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 + i * 0.05, duration: 0.45, ease: EASE }}>
          <span>0{i + 1}</span><Link to={m.to} className={`p2-menu-${m.tone}`}>{m.label}</Link>
        </motion.li>)}</ol>
      </nav>
      <motion.div className="p2-menu-foot" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
        <Link to={CLUB} className="p2-cta">Let’s sew <ArrowRight size={18} aria-hidden="true" /></Link>
        <p className="p2-pen">Creative people,<br />happier people <Heart delay={0.9} /></p>
      </motion.div>
    </motion.div>}
  </AnimatePresence>;
}

/** On the club page the club link replaces Journal and the button goes straight to checkout. */
export function Nav({ club = false }: { club?: boolean }) {
  const menu = useMenu();
  return <header className="p2-nav">
    <Link to={HOME} className="p2-logo">A Few<br />Loose Threads</Link>
    <nav aria-label="Main">
      <Link to={HOME} aria-current={club ? undefined : "page"}>Home</Link>
      <Link to="/about">About</Link>
      <Link to="/videos">Projects</Link>
      {club ? <Link to={CLUB} aria-current="page">The club</Link> : <Link to="/guides">Journal</Link>}
      <Link to="/shop">Shop</Link>
    </nav>
    <Link to="/guides" className="p2-search" aria-label="Search"><Search size={22} /></Link>
    {club
      ? <a href={JOIN_URL} className="p2-cta"><span>Join<span className="p2-cta-more"> the club</span></span> <ArrowRight size={18} aria-hidden="true" /></a>
      : <Link to={CLUB} className="p2-cta">Let’s sew <ArrowRight size={18} aria-hidden="true" /></Link>}
    <button ref={menu.toggle} type="button" className="p2-burger" aria-label="Open menu" aria-expanded={menu.open} aria-controls="p2-menu" onClick={() => menu.setOpen(true)}>
      <svg viewBox="0 0 24 18" aria-hidden="true"><path d="M2 3 H22" /><path d="M2 9 H16" /><path d="M2 15 H22" /></svg>
    </button>
    <Menu menu={menu} />
  </header>;
}

const POLAROIDS = [
  { src: "/concept/gingham-top.jpg", alt: "A blue gingham top with puff sleeves", at: [1186, 246, 318, 326], r: 4, caption: <>Handmade<br />hits different.</> },
  { src: "/concept/label.jpg", alt: "A sewn-in label that says Good Outfits Brighter Days", at: [1380, 512, 270, 372], r: 7, caption: <>Details<br />matter.</> },
] as const;

/** A rectangle with pinking-shear zigzag edges, as an SVG path. */
function pinked(x0: number, y0: number, x1: number, y1: number, tooth = 18) {
  const pts: [number, number][] = [];
  const side = (ax: number, ay: number, bx: number, by: number, nx: number, ny: number) => {
    const n = Math.max(1, Math.round(Math.hypot(bx - ax, by - ay) / tooth));
    for (let i = 0; i < n; i++) {
      const f = i / n, g = (i + 0.5) / n;
      pts.push([ax + (bx - ax) * f, ay + (by - ay) * f]);
      pts.push([ax + (bx - ax) * g + nx * tooth * 0.55, ay + (by - ay) * g + ny * tooth * 0.55]);
    }
  };
  side(x0, y0, x1, y0, 0, -1); side(x1, y0, x1, y1, 1, 0); side(x1, y1, x0, y1, 0, 1); side(x0, y1, x0, y0, -1, 0);
  return `M${pts.map(p => p.map(v => v.toFixed(1)).join(",")).join(" L")}Z`;
}

/** The no-arch frame: a pinked fabric swatch, stitched inside, laid over an offset second swatch. */
function Swatch() {
  return <motion.g initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: EASE }} style={{ transformOrigin: "973px 880px" }}>
    <g transform="translate(34 -34) rotate(4 973 560)"><path d={pinked(700, 230, 1250, 900)} fill="var(--k-extra)" /></g>
    <g transform="rotate(-3 973 560)">
      <path d={pinked(690, 210, 1256, 900)} fill="var(--k-soft)" />
      <rect x="716" y="236" width="514" height="640" fill="none" stroke="var(--k-soft-ink)" strokeOpacity=".4" strokeWidth="2.4" strokeDasharray="12 9" />
    </g>
  </motion.g>;
}

function Scene() {
  const arch = useArchMode();
  return <div className="p2-scene" data-arch={arch}>
    <svg className="p2-paper" viewBox={`${S.x} 0 ${S.w} ${S.h}`} preserveAspectRatio="none" aria-hidden="true">
      {arch === "one"
        ? <motion.path d="M685,880 V305 A288,288 0 0 1 1261,305 V880 Z" fill="var(--k-soft)" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, ease: EASE }} style={{ transformOrigin: "973px 880px" }} />
        : <Swatch />}
      <Torn seed={11} fill="var(--k-strong)" edge={[[1206, 0], [1214, 52], [1252, 108], [1330, 138], [1428, 150], [1560, 118], [1672, 88]]} close={[[1672, 0]]} />
      <Torn seed={23} fill="var(--k-pop)" edge={[[1672, 112], [1560, 140], [1440, 176], [1404, 250], [1392, 420], [1380, 600], [1404, 780], [1430, 941]]} close={[[1672, 941]]} />
      <Torn seed={37} fill="var(--k-paper)" edge={[[668, 870], [820, 840], [1000, 790], [1180, 778], [1320, 792], [1440, 812]]} close={[[1440, 941], [668, 941]]} />
    </svg>
    <svg className="p2-dashes" viewBox={`${S.x} 0 ${S.w} ${S.h}`} preserveAspectRatio="none" aria-hidden="true">
      <Draw dash d="M1196,0 C1180,40 1188,92 1236,118" delay={1.2} width={2.6} />
      <Draw dash d="M1468,388 C1520,430 1534,500 1504,570" delay={1.3} width={2.6} />
      <Draw dash d="M1390,770 C1320,700 1220,620 1140,640 C1080,660 1090,740 1170,760 C1260,780 1330,720 1350,640 C1360,600 1352,560 1345,540" delay={1.4} width={2.6} />
    </svg>

    <motion.img className="p2-abs p2-chelsea" src="/chelsea-cutout-trim.webp" alt="Chelsea smiling with one arm raised, in a straw hat and a colorful checked sundress"
      style={sbox(712, 76, 309, 846)} initial={{ opacity: 0, y: 26 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25, duration: 0.7, ease: EASE }} />

    <svg className="p2-abs p2-burst" style={sbox(1000, 62, 60, 76)} viewBox="0 0 60 76" aria-hidden="true">
      <Draw d="M14 40 L30 6" delay={1.0} width={3.6} /><Draw d="M22 52 L50 34" delay={1.08} width={3.6} /><Draw d="M28 68 L56 70" delay={1.16} width={3.6} />
    </svg>

    <Note className="p2-script p2-same" style={sbox(1046, 250)} delay={1.3}>
      <span>Same</span><span>girl....</span><span>more</span><span>fabric</span><span>please.</span><Heart delay={1.8} />
    </Note>

    <motion.div className="p2-abs p2-card" style={sbox(1160, 70, 270, 248)} initial={{ opacity: 0, y: -20, rotate: -12 }} animate={{ opacity: 1, y: 0, rotate: -6 }} transition={{ delay: 0.7, type: "spring", stiffness: 150, damping: 15 }}>
      <ul aria-label="The plan">
        {["Sew things", "Be inspired", "Wear the joy", "Repeat"].map((t, i) => <li key={t}>
          <svg viewBox="0 0 24 24" aria-hidden="true"><rect x="2" y="2" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.6" />{i === 0 && <Draw d="M6 12 L10 17 L20 3" delay={1.4} width={2.6} />}</svg>
          {t}{i === 3 && <Heart delay={1.6} />}
        </li>)}
      </ul>
    </motion.div>

    {POLAROIDS.map((p, i) => <motion.figure key={p.src} className="p2-abs p2-polaroid" style={sbox(p.at[0], p.at[1], p.at[2], p.at[3])}
      initial={{ opacity: 0, y: 40, rotate: p.r + 10 }} animate={{ opacity: 1, y: 0, rotate: p.r }} whileHover={{ y: -6, rotate: p.r / 2 }}
      transition={{ delay: 0.85 + i * 0.15, type: "spring", stiffness: 140, damping: 15 }}>
      <span className="p2-polaroid-img"><img src={p.src} alt={p.alt} /></span>
      <figcaption>{p.caption}<Heart delay={1.5 + i * 0.2} /></figcaption>
    </motion.figure>)}

    <Note className="p2-caps" style={sbox(1540, 286)} delay={1.5}>Bolder<br />Brighter<br />Happier<br />Handmade<Heart delay={1.9} /></Note>

    <Note className="p2-sig" style={sbox(1054, 786)} delay={1.6}>
      <p className="p2-sig-name">Chelsea <Heart delay={2.1} /></p>
      <p className="p2-sig-role">Founder, A Few Loose Threads</p>
    </Note>
  </div>;
}

export function Hero() {
  return <section className="p2-hero" aria-labelledby="p2-title">
    <div className="p2-stage">
      <svg className="p2-left-paper" viewBox="0 0 1672 941" preserveAspectRatio="none" aria-hidden="true">
        <Torn seed={5} fill="var(--k-strong)" edge={[[0, 752], [150, 768], [310, 784], [470, 806], [600, 868], [700, 941]]} close={[[0, 941]]} />
      </svg>
      <svg className="p2-left-dash" viewBox="0 0 1672 941" preserveAspectRatio="none" aria-hidden="true">
        <Draw dash d="M0,912 C60,900 100,870 112,840 C130,800 90,792 100,822 C 80,790 60,810 84,850 C110,890 200,880 240,840 C 270,810 250,770 210,800" delay={1.5} width={2.4} />
      </svg>
      <Nav />
      <div className="p2-copy">
        <h1 id="p2-title">
          {["Color", "outside", "the pattern."].map((w, i) => <motion.span key={w} className={`p2-h-${i}`} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + i * 0.12, duration: 0.6, ease: EASE }}>{w}</motion.span>)}
          <svg className="p2-underline" viewBox="0 0 300 18" preserveAspectRatio="none" aria-hidden="true"><Draw d="M4 12 C 80 6, 200 6, 296 9" delay={0.8} width={7} /></svg>
        </h1>
        <motion.div className="p2-copy-rest" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.5, ease: EASE }}>
          <p className="p2-sub">Sewing, style, and a more colorful life.</p>
          <p className="p2-lede">A Few Loose Threads is where modern sewing meets real life: creative projects, personal style, and a little encouragement to make it your own.</p>
          <div className="p2-actions">
            <a href="#making" className="p2-btn p2-btn-blue">Explore projects <ArrowRight size={18} aria-hidden="true" /></a>
            <Link to="/about" className="p2-btn p2-btn-line">Meet Chelsea</Link>
          </div>
        </motion.div>
      </div>
      <Note className="p2-script p2-creative" style={{ left: "21%", top: "88%" }} delay={1.7}>
        <span>Creative</span><span>People</span><span>Happier</span><span>People <Heart delay={2.2} /></span>
      </Note>
      <Scene />
    </div>
  </section>;
}
